import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { CreateRequiredFileRequest, AccountFilter } from '../../types';
import { FILE_TYPES } from '../../constants/constants';
import { SearchableSelect } from '../../components/SearchableSelect';
import { normalizeFileType, denormalizeFileType } from '../../utils/fileTypeUtils';

interface CreateRequiredFileModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (requirementData: CreateRequiredFileRequest) => void;
  case_id: string;
}

const CreateRequiredFileModal: React.FC<CreateRequiredFileModalProps> = ({ 
  show, 
  onHide, 
  onSubmit, 
  case_id 
}) => {
  const [validated, setValidated] = useState(false);
  const [selectedFileTypeUI, setSelectedFileTypeUI] = useState<string>(''); // UI display name
  const [formData, setFormData] = useState<CreateRequiredFileRequest>({
    case_id,
    category: '',
    file_type: '',
    description: '',
    required_count: 1,
    notes: ''
  });

  const [showDateRange, setShowDateRange] = useState(false);
  const [showAccountFilter, setShowAccountFilter] = useState(false);
  const [showCheckNumbers, setShowCheckNumbers] = useState(false);
  const [checkNumbersInput, setCheckNumbersInput] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [accountFilter, setAccountFilter] = useState<AccountFilter>({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setFormData({
        case_id,
        category: '',
        file_type: '',
        description: '',
        required_count: 1,
        notes: ''
      });
      setShowCheckNumbers(false);
      setCheckNumbersInput('');
      setSelectedFileTypeUI('');
      setDateRange({ from: '', to: '' });
      setAccountFilter({});
      setShowDateRange(false);
      setShowAccountFilter(false);
      setValidated(false);
    }
  }, [show, case_id]);

  // Update normalized file_type and showDateRange when UI selection changes
  useEffect(() => {
    if (selectedFileTypeUI) {
      const normalized = normalizeFileType(selectedFileTypeUI);
      setFormData(prev => ({ ...prev, file_type: normalized }));
      
      // Show date range for bank statements
      if (normalized === 'bank_statement') {
        setShowDateRange(true);
      } else if (normalized === 'check_image') {
        // Show the check number option for check images
        setShowCheckNumbers(true);
      } else {
        setShowDateRange(false);
        setDateRange({ from: '', to: '' });
        setShowCheckNumbers(false);
        setCheckNumbersInput('');
      }
    } else {
      setFormData(prev => ({ ...prev, file_type: '' }));
      setShowDateRange(false);
      setDateRange({ from: '', to: '' });
      setShowCheckNumbers(false);
      setCheckNumbersInput('');
    }
  }, [selectedFileTypeUI]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const form = e.currentTarget as HTMLFormElement;
    if (!form.checkValidity() || !selectedFileTypeUI) {
      setValidated(true);
      if (!selectedFileTypeUI) {
        return;
      }
    }

    // Normalize the file type before submitting
    const normalizedFileType = normalizeFileType(selectedFileTypeUI);
    
    // Derive category from file type
    let category = 'Other';
    if (selectedFileTypeUI === 'Bank Statements' || normalizedFileType === 'bank_statement') {
      category = 'Bank Statements';
    } else if (selectedFileTypeUI === 'Check Images' || normalizedFileType === 'check_image') {
      category = 'Supporting Documents';
    } else if (selectedFileTypeUI.startsWith('IRS Tax form') || normalizedFileType.startsWith('irs_tax_form')) {
      category = 'Financial Records';
    } else if (selectedFileTypeUI.startsWith('Financial statement') || normalizedFileType.startsWith('financial_statement')) {
      category = 'Financial Records';
    } else if (normalizedFileType === 'invoice' || normalizedFileType === 'expense_report') {
      category = 'Financial Records';
    } else if (normalizedFileType === 'legal_document' || normalizedFileType === 'lease_agreement') {
      category = 'Legal Documents';
    }

    const requirementData: CreateRequiredFileRequest = {
      case_id: formData.case_id,
      category: category,
      file_type: normalizedFileType,
      description: formData.description.trim(),
    };

    // Add date_range for bank statements
    if (formData.file_type === 'bank_statement' && dateRange.from && dateRange.to) {
      requirementData.date_range = dateRange;
      // Don't include required_count for bank statements
    } else if (formData.file_type && formData.file_type !== 'bank_statement') {
      // Only include required_count for non-bank-statement files
      requirementData.required_count = formData.required_count || 1;
    }

    // Add account_filter if provided
    if (showAccountFilter && (accountFilter.bank_name || accountFilter.account_number)) {
      requirementData.account_filter = accountFilter;
    }

    // Add check_numbers for check images if provided
    if (showCheckNumbers && checkNumbersInput && checkNumbersInput.trim()) {
      const parsed = checkNumbersInput
        .split(',')
        .map(s => s.trim())
        .filter(s => s !== '');
      if (parsed.length > 0) {
        requirementData.check_numbers = parsed;
      }
    }

    // Add notes if provided
    if (formData.notes && formData.notes.trim()) {
      requirementData.notes = formData.notes.trim();
    }

    onSubmit(requirementData);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Required File</Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>File Type <span className="text-danger">*</span></Form.Label>
            <SearchableSelect
              value={selectedFileTypeUI}
              options={FILE_TYPES}
              onChange={setSelectedFileTypeUI}
              placeholder="Type to search file types..."
            />
            {validated && !selectedFileTypeUI && (
              <div className="invalid-feedback d-block">
                Please select a file type
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description <span className="text-danger">*</span></Form.Label>
            <Form.Control
              required
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Monthly bank statements for Q1 2024"
            />
            <Form.Control.Feedback type="invalid">
              Please enter a description
            </Form.Control.Feedback>
          </Form.Group>

          {/* Date Range for Bank Statements */}
          {formData.file_type === 'bank_statement' && (
            <div className="mb-3 p-3 border rounded bg-light">
              <Form.Check
                type="checkbox"
                label="Specify Date Range"
                checked={showDateRange}
                onChange={(e) => setShowDateRange(e.target.checked)}
              />
              {showDateRange && (
                <div className="row mt-2">
                  <div className="col-md-6">
                    <Form.Label>From Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={dateRange.from}
                      onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <Form.Label>To Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={dateRange.to}
                      onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Check Numbers for Check Images */}
          {showCheckNumbers && (
            <div className="mb-3 p-3 border rounded bg-light">
              <Form.Check
                type="checkbox"
                label="Match specific check numbers (optional)"
                checked={showCheckNumbers}
                onChange={() => {
                  // toggle handled by selecting file type; keep checkbox for UX but do nothing here
                }}
              />
              <Form.Label className="mt-2">Check number(s) (comma-separated)</Form.Label>
              <Form.Control
                type="text"
                value={checkNumbersInput}
                onChange={(e) => setCheckNumbersInput(e.target.value)}
                placeholder="e.g., 12345, 67890"
              />
              <Form.Text className="text-muted">
                Optional: list specific check numbers to match against check images.
              </Form.Text>
            </div>
          )}

          {/* Required Count for Non-Bank-Statement Files */}
          {formData.file_type && formData.file_type !== 'bank_statement' && (
            <Form.Group className="mb-3">
              <Form.Label>Required Count</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={formData.required_count || 1}
                onChange={(e) => setFormData({ ...formData, required_count: parseInt(e.target.value) || 1 })}
              />
              <Form.Text className="text-muted">
                Number of files needed to satisfy this requirement
              </Form.Text>
            </Form.Group>
          )}

          {/* Account Filter */}
          {formData.file_type === 'bank_statement' && (
            <div className="mb-3 p-3 border rounded bg-light">
              <Form.Check
                type="checkbox"
                label="Add Account Filter (Optional)"
                checked={showAccountFilter}
                onChange={(e) => setShowAccountFilter(e.target.checked)}
              />
              {showAccountFilter && (
                <div className="row mt-2">
                  <div className="col-md-6">
                    <Form.Label>Bank Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={accountFilter.bank_name || ''}
                      onChange={(e) => setAccountFilter({ ...accountFilter, bank_name: e.target.value })}
                      placeholder="e.g., Chase Bank"
                    />
                  </div>
                  <div className="col-md-6">
                    <Form.Label>Account Number (Last 4 digits)</Form.Label>
                    <Form.Control
                      type="text"
                      value={accountFilter.account_number || ''}
                      onChange={(e) => setAccountFilter({ ...accountFilter, account_number: e.target.value })}
                      placeholder="e.g., 1234"
                      maxLength={4}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Notes (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes or instructions"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Create Requirement
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateRequiredFileModal;

